/* eslint react/sort-comp: 0 */
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import throttle from 'lodash/throttle';
import ErrorBoundary from '../Utils/ErrorBoundary/ErrorBoundary';
import StaticHeader from '../StaticHeader/HeaderClassUpdater';
import Suspense from '../Utils/SuspenseWrapper/SuspenseWrapper.tsx';

import titleExtractor from '../../utils/titleExtractor.js';
import actions from '../../actions.js';
import navigationActions from '../Navigation/actions.js';
import { TAB } from '../Navigation/Menu/menuConfig';

import {
  getAddress,
  getAddressStatus,
  getDemographicInfo,
  getDemographicInfoStatus,
  getDuplicateDistricts,
  getGeoHierarchy,
  getGeoHierarchyStatus,
  getHeaderVisibility,
  getHierarchyLevel,
  getIsNavigationHeaderFolded,
  getIsSidebarDesktopExpanded,
  getPriceHistory,
  getPriceHistoryStatus,
  getRealEstateType,
  getStaticMapsStatus,
  getUserIntent,
  getZipCode,
  isBurgerMenuOpened,
  isMobileNavigationMenuOpen,
  isSidebarExpanded,
  isUserIntentAndHierarchyForNewDashboard,
} from '../../selectors/selectors.js';

import styles from '../../styles.less';
import SidebarExpandedMobile from '../Navigation/SidebarExpandedMobile/SidebarExpandedMobile';

import { Desktop, Mobile, Tablet } from '../Utils/MediaComponents/MediaComponents';
import NavigationSidebarDesktop from '../Navigation/Sidebar/NavigationSidebarDesktop';
import NavigationSidebarTablet from '../Navigation/Sidebar/NavigationSidebarTablet';
import NavigationSidebarMobile from '../Navigation/Sidebar/NavigationSidebarMobile';
import GeoHierarchyPageTab from './GeoHierarchyPageTab';
import Dashboard from '../Dashboard/Dashboard';
import NewDashboard from '../Dashboard/NewDashboard';
import { isFeatureOn } from "../../utils/featureSwitchReader";
import ContainerSearchMobile from "../Navigation/Sidebar/ContainerSearchMobile/ContainerSearchMobile";
import WebMetrics from "../Utils/WebMetrics/WebMetrics";

export const LazyPriceCheckDetailed = React.lazy(() =>
  import(/* webpackChunkName: "PriceCheckDetailed" */ '../PriceCheck/Detailed/Detailed'),
);
export const LazyPriceHistoryDetailed = React.lazy(() =>
  import(/* webpackChunkName: "PriceHistory" */ '../PriceHistory/Detailed/PriceHistoryDetailed'),
);
export const LazyDemandChecker = React.lazy(() =>
  import(/* webpackChunkName: "DemandChecker" */ '../DemandChecker/DemandChecker'),
);

export const GeoHierarchyPage = (props) => {
  const location = useLocation();

  const scrollListener = (props) => {
    return () => props.scrollY(window.pageYOffset || document.documentElement.scrollTop);
  };

  const resizeListener = (props) => {
    return () => props.setWindowWidth(window.innerWidth);
  };

  const scrollListenerThrottled = throttle(scrollListener(props), 100);
  const resizeListenerThrottled = throttle(resizeListener(props), 100);
  const [isComponentMounted, setIsComponentMounted] = useState(false);

  useEffect(() => {
    window.addEventListener('scroll', scrollListenerThrottled);
    window.addEventListener('resize', resizeListenerThrottled);
    setIsComponentMounted(true);

    return () => {
      window.removeEventListener('scroll', scrollListenerThrottled);
      window.removeEventListener('resize', resizeListenerThrottled);
      setIsComponentMounted(false);
    };
  }, [scrollListenerThrottled, resizeListenerThrottled]);

  const isDesktopSidebarExpanded = () => {
    return isComponentMounted ? props.isSidebarDesktopExpanded : true;
  }

  const renderSidebar = (location) => {
    const hasTitle =
      props.hierarchyLevel &&
      props.geoHierarchy &&
      (props.address || props.realEstateType !== 'address');

    if (!hasTitle) {
      return null;
    }

    const { title, subTitle } = hasTitle
      ? titleExtractor(
        props.hierarchyLevel,
        props.geoHierarchy,
        props.address,
        props.duplicateDistricts,
      )
      : { title: '', subTitle: '' };

    const {
      isNavigationHeaderFolded,
      visibleHeader,
      sidebarExpanded,
      foldSidebar,
    } = props;

    const widthToUse = !isDesktopSidebarExpanded() ? styles.collapsedDesktopSidebar : styles.expandedDesktopSidebar;

    const sidebarMobileClasses = `grid-item ${styles.sidebar} ${
      visibleHeader ? styles.sidebar_mobile : styles.sidebar_mobile_top
    }`;

    return (
      <>
        <Desktop
          render={() => (
            <div
              id="sidebar"
              className={`grid-item ${widthToUse} ${styles.sidebar} ${visibleHeader ? styles.marginTop : ''}`}
            >
              <ErrorBoundary>
                <NavigationSidebarDesktop
                  title={title}
                  subTitle={subTitle}
                  isExpanded={sidebarExpanded}
                  onFocusOut={() => foldSidebar()}
                />
              </ErrorBoundary>
            </div>
          )}
        />
        <Tablet
          render={() => (
            <div
              id="sidebar"
              className={`grid-item ${styles.sidebar} ${sidebarExpanded ? styles.expandedSidebar : ''} ${
                visibleHeader ? styles.marginTop : ''
              }`}
            >
              <ErrorBoundary>
                <NavigationSidebarTablet
                  title={title}
                  subTitle={subTitle}
                  isExpanded={sidebarExpanded}
                  onFocusOut={() => foldSidebar()}
                />
              </ErrorBoundary>
            </div>
          )}
        />
        <Mobile
          render={() => (
            <div>
              <div id="sidebar" className={sidebarMobileClasses}>
                <ErrorBoundary>
                  <NavigationSidebarMobile
                    title={title}
                    location={location}
                    isNavigationHeaderFolded={isNavigationHeaderFolded}
                  />
                </ErrorBoundary>
              </div>
            </div>
          )}
        />
      </>
    );
  };

  const createContent = () => {
    const {
      addressStatus,
      geoHierarchyStatus,
      priceHistoryStatus,
      staticMapsStatus,
      isSidebarDesktopExpanded,
      userIntentAndHierarchyForNewDashboard,
    } = props;
    const commonStatusesList = [addressStatus, geoHierarchyStatus, priceHistoryStatus];

    return (
      <div className={`grid ${styles.contentContainer}`}>
        <WebMetrics reportOnRoute="/" />
        {renderSidebar(location)}
        <Routes>
          <Route
            path="/"
            element={
              <GeoHierarchyPageTab
                tabId={TAB.home.id}
                render={() => (userIntentAndHierarchyForNewDashboard ? <NewDashboard /> : <Dashboard />)}
                isVisible={TAB.home.isActive(location.pathname)}
                isSidebarDesktopExpanded={isSidebarDesktopExpanded}
              />}
          />

          <Route
            path={`/${TAB.priceHistory.name}`}
            element={
              <GeoHierarchyPageTab
                tabId={TAB.priceHistory.id}
                statuses={[...commonStatusesList, staticMapsStatus]}
                render={() => (
                  <Suspense fallback={<div />}>
                    <LazyPriceHistoryDetailed />
                  </Suspense>
                )}
                isSidebarDesktopExpanded={isSidebarDesktopExpanded}
                isVisible={TAB.priceHistory.isActive(location.pathname)}
              />
            }
          />

          <Route
            path={`/${TAB.priceCheck.name}`}
            element={
              <GeoHierarchyPageTab
                tabId={TAB.priceCheck.id}
                statuses={[...commonStatusesList, staticMapsStatus]}
                render={() => (
                  <Suspense fallback={<div />}>
                    <LazyPriceCheckDetailed />
                  </Suspense>
                )}
                isSidebarDesktopExpanded={isSidebarDesktopExpanded}
                isVisible={TAB.priceCheck.isActive(location.pathname)}
              />
            }
          />

          <Route
            path={`/${TAB.selling.name}`}
            element={
              <GeoHierarchyPageTab
                tabId={TAB.selling.id}
                render={() => (
                  <Suspense fallback={<div />}>
                    <LazyDemandChecker hostApplication="atlas" />
                  </Suspense>
                )}
                isSidebarDesktopExpanded={isSidebarDesktopExpanded}
                isVisible={TAB.selling.isActive(location.pathname)}
              />
            }
          />

          <Route
            path="*"
            element={<Navigate to="/" />}
          />
        </Routes>
      </div>
    )
      ;
  };

  const { isBurgerMenuOpen, isMobileMenuOpen } = props;

  if (isBurgerMenuOpen) {
    return <SidebarExpandedMobile />;
  }

  return (
    <>
      <StaticHeader />
      {createContent()}
      {isMobileMenuOpen && <ContainerSearchMobile />}
    </>
  );
};

const mapStateToProps = (state) => ({
  staticMapsStatus: getStaticMapsStatus(state),
  address: getAddress(state),
  addressStatus: getAddressStatus(state),
  geoHierarchy: getGeoHierarchy(state),
  geoHierarchyStatus: getGeoHierarchyStatus(state),
  priceHistory: getPriceHistory(state),
  priceHistoryStatus: getPriceHistoryStatus(state),
  realEstateType: getRealEstateType(state),
  hierarchyLevel: getHierarchyLevel(state),
  userIntentAndHierarchyForNewDashboard: isUserIntentAndHierarchyForNewDashboard(state),
  demographicInfo: getDemographicInfo(state),
  demographicInfoStatus: getDemographicInfoStatus(state),
  sidebarExpanded: isSidebarExpanded(state),
  visibleHeader: getHeaderVisibility(state),
  zipCode: getZipCode(state),
  isBurgerMenuOpen: isBurgerMenuOpened(state),
  isMobileMenuOpen: isMobileNavigationMenuOpen(state),
  isNavigationHeaderFolded: getIsNavigationHeaderFolded(state),
  isSidebarDesktopExpanded: getIsSidebarDesktopExpanded(state),
  userIntent: getUserIntent(state),
  duplicateDistricts: getDuplicateDistricts(state),
});

const mapDispatchToProps = {
  scrollY: (y) => actions.scrollY(y),
  setWindowWidth: (width) => actions.setWindowWidth(width),
  foldSidebar: () => navigationActions.toggleSidebar(false),
};

GeoHierarchyPage.defaultProps = {
  isFeatureOn,
};

export default connect(mapStateToProps, mapDispatchToProps)(GeoHierarchyPage);
